const RETRYABLE_STATUSES = new Set([429, 500, 502, 503, 504]);  // 503 is common under load — always transient
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
const LOCAL_GEMINI_KEY = String(process.env.REACT_APP_GEMINI_API_KEY || "")
  .trim()
  .replace(/^['"]+|['"]+$/g, "");
const USE_LOCAL_DIRECT_GEMINI_FALLBACK = !API_BASE_URL && !!LOCAL_GEMINI_KEY;
let IMAGEN_SUPPORTS_NEGATIVE_PROMPT = true;
let IMAGEN_SUPPORTS_ASPECT_RATIO = true;

export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = src;
  });
}

/**
 * Best-effort heuristic to detect composite outputs (diptych / triptych /
 * front+back in one frame) that Imagen occasionally generates despite the
 * single-view instruction.
 *
 * Two independent signals are checked:
 *  1. Aspect-ratio trap — a 9:16 request that comes back wider than ~0.75
 *     (i.e. closer to square or landscape) is almost certainly a diptych.
 *  2. Pixel seam detector — looks for a strong vertical brightness discontinuity
 *     at the centre (diptych) or at the 1/3 and 2/3 positions (triptych).
 *     Thresholds are deliberately conservative so that monochromatic velvet-
 *     style garments (where the split-line colour difference is subtle) still
 *     get caught.
 */
export async function looksLikeSplitComposite(imageUrl) {
  if (
    typeof window === "undefined" ||
    typeof document === "undefined" ||
    typeof Image === "undefined"
  ) {
    return false;
  }
  if (!imageUrl || typeof imageUrl !== "string") return false;

  try {
    const img = await loadImageElement(imageUrl);
    const w = img.naturalWidth || img.width || 0;
    const h = img.naturalHeight || img.height || 0;
    if (w < 256 || h < 256) return false;

    // ── Signal 1: aspect-ratio trap ──────────────────────────────────────────
    // We always request 9:16 (≈ 0.5625). A returned image wider than 0.72
    // strongly suggests Imagen composed a side-by-side panel instead.
    const aspectRatio = w / h;
    if (aspectRatio > 0.72) return true;

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return false;

    ctx.drawImage(img, 0, 0, w, h);
    const pixels = ctx.getImageData(0, 0, w, h).data;

    const yStep = Math.max(2, Math.floor(h / 180));
    const xStep = Math.max(1, Math.floor(w / 220));

    // ── Signal 2: vertical seam brightness discontinuity ─────────────────────
    // Lower diff threshold (80 → was 110) and reduce coherence requirements so
    // low-contrast seams in monochromatic velvet tones are still detected.
    const seamMetric = (x) => {
      if (x < 1 || x >= w - 2) return { score: 0, coherence: 0 };
      let sum = 0;
      let high = 0;
      let samples = 0;
      for (let y = 0; y < h; y += yStep) {
        const left = (y * w + x) * 4;
        const right = left + 4;
        const diff =
          Math.abs(pixels[left]     - pixels[right]) +
          Math.abs(pixels[left + 1] - pixels[right + 1]) +
          Math.abs(pixels[left + 2] - pixels[right + 2]);
        sum += diff;
        if (diff > 80) high += 1;   // was 110 — lowered for subtle seams
        samples += 1;
      }
      if (!samples) return { score: 0, coherence: 0 };
      const avg = sum / samples;
      const coherence = high / samples;
      return { score: avg * (0.6 + coherence), coherence };
    };

    // ── Check for centre seam (diptych) ─────────────────────────────────────
    const centerStart = Math.floor(w * 0.34);
    const centerEnd   = Math.floor(w * 0.66);
    let bestCenter = { score: 0, coherence: 0 };
    for (let x = centerStart; x <= centerEnd; x += xStep) {
      const m = seamMetric(x);
      if (m.score > bestCenter.score) bestCenter = m;
    }

    const edgeScores = [];
    const collect = (start, end) => {
      for (let x = start; x <= end; x += xStep) {
        edgeScores.push(seamMetric(x).score);
      }
    };
    collect(Math.floor(w * 0.08), Math.floor(w * 0.24));
    collect(Math.floor(w * 0.76), Math.floor(w * 0.92));
    if (!edgeScores.length) return false;

    edgeScores.sort((a, b) => a - b);
    const edgeMedian = edgeScores[Math.floor(edgeScores.length / 2)] || 0;
    if (edgeMedian <= 0) return false;

    const centerToEdgeRatio = bestCenter.score / edgeMedian;
    // Lowered ratio threshold 1.7 → 1.4 and coherence 0.58 → 0.45
    const isDiptych = centerToEdgeRatio >= 1.4 && bestCenter.coherence >= 0.45;
    if (isDiptych) return true;

    // ── Check for triptych — two seams at ~33% and ~66% ──────────────────────
    let bestThirdLeft  = { score: 0, coherence: 0 };
    let bestThirdRight = { score: 0, coherence: 0 };
    const thirdL_start = Math.floor(w * 0.26);
    const thirdL_end   = Math.floor(w * 0.40);
    const thirdR_start = Math.floor(w * 0.60);
    const thirdR_end   = Math.floor(w * 0.74);
    for (let x = thirdL_start; x <= thirdL_end; x += xStep) {
      const m = seamMetric(x);
      if (m.score > bestThirdLeft.score) bestThirdLeft = m;
    }
    for (let x = thirdR_start; x <= thirdR_end; x += xStep) {
      const m = seamMetric(x);
      if (m.score > bestThirdRight.score) bestThirdRight = m;
    }
    // Lowered triptych ratio 1.5 → 1.3 and coherence 0.45 → 0.35
    const leftRatio  = edgeMedian > 0 ? bestThirdLeft.score  / edgeMedian : 0;
    const rightRatio = edgeMedian > 0 ? bestThirdRight.score / edgeMedian : 0;
    const isTriptych = leftRatio >= 1.3 && rightRatio >= 1.3 &&
                       bestThirdLeft.coherence >= 0.35 && bestThirdRight.coherence >= 0.35;
    if (isTriptych) return true;

    // ── Signal 3: horizontal seam detector (top/bottom composite) ────────────
    // Imagen occasionally stacks front view on top, back view below in a single
    // tall frame. The vertical seam detector misses this entirely.
    // Scan horizontal rows at centre (50%) and thirds (33%, 66%) for brightness
    // discontinuity running across the full width of the image.
    const hSeamMetric = (y) => {
      if (y < 1 || y >= h - 2) return { score: 0, coherence: 0 };
      let sum = 0;
      let high = 0;
      let samples = 0;
      for (let x = 0; x < w; x += xStep) {
        const top    = (y * w + x) * 4;
        const bottom = ((y + 1) * w + x) * 4;
        const diff =
          Math.abs(pixels[top]     - pixels[bottom]) +
          Math.abs(pixels[top + 1] - pixels[bottom + 1]) +
          Math.abs(pixels[top + 2] - pixels[bottom + 2]);
        sum += diff;
        if (diff > 80) high += 1;
        samples += 1;
      }
      if (!samples) return { score: 0, coherence: 0 };
      const avg = sum / samples;
      const coherence = high / samples;
      return { score: avg * (0.6 + coherence), coherence };
    };

    // Sample horizontal edge rows (top and bottom quarters) for baseline
    const hEdgeScores = [];
    const hCollect = (startY, endY) => {
      const step = Math.max(1, Math.floor(h / 80));
      for (let y = startY; y <= endY; y += step) {
        hEdgeScores.push(hSeamMetric(y).score);
      }
    };
    hCollect(Math.floor(h * 0.05), Math.floor(h * 0.18));
    hCollect(Math.floor(h * 0.82), Math.floor(h * 0.95));
    if (hEdgeScores.length > 0) {
      hEdgeScores.sort((a, b) => a - b);
      const hEdgeMedian = hEdgeScores[Math.floor(hEdgeScores.length / 2)] || 0;
      if (hEdgeMedian > 0) {
        // Check centre horizontal seam
        const hCenterStart = Math.floor(h * 0.38);
        const hCenterEnd   = Math.floor(h * 0.62);
        const hStep = Math.max(1, Math.floor(h / 80));
        let bestHCenter = { score: 0, coherence: 0 };
        for (let y = hCenterStart; y <= hCenterEnd; y += hStep) {
          const m = hSeamMetric(y);
          if (m.score > bestHCenter.score) bestHCenter = m;
        }
        const hCenterRatio = bestHCenter.score / hEdgeMedian;
        if (hCenterRatio >= 1.4 && bestHCenter.coherence >= 0.45) return true;
      }
    }

    // ── Signal 4: back-view detector ─────────────────────────────────────────
    // If the model is facing away, there's no face in the upper-centre region.
    // Skin tones have high R, moderate G, low B (R > 100, R > G > B, R-B > 40).
    // Sample the upper-centre zone (x: 30–70%, y: 5–30%) and count skin pixels.
    // A genuine front-view fashion image will almost always have a face there.
    const skinCheckX1 = Math.floor(w * 0.30);
    const skinCheckX2 = Math.floor(w * 0.70);
    const skinCheckY1 = Math.floor(h * 0.05);
    const skinCheckY2 = Math.floor(h * 0.30);
    let skinPixels = 0;
    let totalChecked = 0;
    const skinXStep = Math.max(1, Math.floor((skinCheckX2 - skinCheckX1) / 40));
    const skinYStep = Math.max(1, Math.floor((skinCheckY2 - skinCheckY1) / 40));
    for (let y = skinCheckY1; y < skinCheckY2; y += skinYStep) {
      for (let x = skinCheckX1; x < skinCheckX2; x += skinXStep) {
        const i = (y * w + x) * 4;
        const r = pixels[i], g = pixels[i + 1], b = pixels[i + 2];
        // Skin tone heuristic: high red, red > green > blue, meaningful R-B gap
        if (r > 100 && r > g && g > b && (r - b) > 40 && g > 50) {
          skinPixels++;
        }
        totalChecked++;
      }
    }
    if (totalChecked > 0) {
      const skinRatio = skinPixels / totalChecked;
      // Less than 1.5% skin tone in the face zone → likely a back view
      if (skinRatio < 0.015) return true;
    }

    return false;
  } catch (_err) {
    return false;
  }
}

function getApiMessage(data) {
  return data?.error?.message || data?.message || "";
}

function isUnsupportedSetting(apiMessage, settingPattern) {
  const msg = String(apiMessage || "");
  if (!msg) return false;
  return (
    settingPattern.test(msg) &&
    /(no longer supported|not supported|unsupported|unknown|invalid)/i.test(msg)
  );
}

function getUserMessage(status, apiMessage) {
  if (/api key not valid/i.test(apiMessage)) {
    return "Server Gemini key is invalid. Update GEMINI_API_KEY on the backend.";
  }
  if (status === 401 || status === 403) {
    return "Imagen request not authorized. Check backend key configuration.";
  }
  if (status === 404) {
    return "Imagen proxy endpoint not found. Start backend proxy or set REACT_APP_API_BASE_URL.";
  }
  if (status === 400) {
    return apiMessage
      ? `Imagen rejected request: ${apiMessage}`
      : "Prompt was rejected. Try shorter, simpler wording and generate again.";
  }
  if (status === 429) {
    return "Render queue is busy. Retrying automatically...";
  }
  if (status === 503) {
    return "Imagen is busy — retrying automatically. This can happen under high load.";
  }
  if (status >= 500) {
    return "Imagen is temporarily unavailable. Retrying automatically...";
  }
  if (apiMessage) {
    return apiMessage;
  }
  return "Imagen request failed. Please try again.";
}

export async function callImagenWithRetry({
  prompt,
  negativePrompt = "",
  aspectRatio = undefined,
  retries = 7,
  initialDelayMs = 2500,
  requestTimeoutMs = 55000,
}) {
  if (!prompt || !String(prompt).trim()) {
    const err = new Error("Missing prompt for Imagen request");
    err.userMessage = "Cannot generate without a prompt. Please update design details and retry.";
    throw err;
  }

  const normalizedNegativePrompt = String(negativePrompt || "").trim();
  const normalizedAspectRatio = aspectRatio ? String(aspectRatio).trim() : "";
  let includeNegativePrompt = !!normalizedNegativePrompt && IMAGEN_SUPPORTS_NEGATIVE_PROMPT;
  let includeAspectRatio = !!normalizedAspectRatio && IMAGEN_SUPPORTS_ASPECT_RATIO;

  let delay = initialDelayMs;
  for (let i = 0; i < retries; i++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), requestTimeoutMs);

    try {
      const response = await fetch(
        USE_LOCAL_DIRECT_GEMINI_FALLBACK
          ? `https://generativelanguage.googleapis.com/v1beta/models/imagen-4.0-generate-001:predict?key=${LOCAL_GEMINI_KEY}`
          : `${API_BASE_URL}/api/imagen`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          signal: controller.signal,
          body: JSON.stringify(
            USE_LOCAL_DIRECT_GEMINI_FALLBACK
              ? {
                  instances: [{ prompt }],
                  parameters: {
                    sampleCount: 1,
                    ...(includeNegativePrompt ? { negativePrompt: normalizedNegativePrompt } : {}),
                    ...(includeAspectRatio ? { aspectRatio: normalizedAspectRatio } : {}),
                  },
                }
              : {
                  prompt,
                  ...(includeNegativePrompt ? { negativePrompt: normalizedNegativePrompt } : {}),
                  ...(includeAspectRatio ? { aspectRatio: normalizedAspectRatio } : {}),
                }
          ),
        }
      );

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const status = response.status;
        const apiMessage = getApiMessage(data);
        if (status === 400) {
          const negativePromptUnsupported = includeNegativePrompt &&
            isUnsupportedSetting(apiMessage, /negative\s*prompt|negativePrompt/i);
          const aspectRatioUnsupported = includeAspectRatio &&
            isUnsupportedSetting(apiMessage, /aspect\s*ratio|aspectRatio/i);
          if (negativePromptUnsupported || aspectRatioUnsupported) {
            if (negativePromptUnsupported) {
              includeNegativePrompt = false;
              IMAGEN_SUPPORTS_NEGATIVE_PROMPT = false;
            }
            if (aspectRatioUnsupported) {
              includeAspectRatio = false;
              IMAGEN_SUPPORTS_ASPECT_RATIO = false;
            }
            continue;
          }
        }
        const err = new Error(
          `Imagen API error: HTTP ${status}${apiMessage ? ` - ${apiMessage}` : ""}`
        );
        err.status = status;
        err.retryable = RETRYABLE_STATUSES.has(status);
        err.userMessage = getUserMessage(status, apiMessage);
        err.retryAfter = response.headers.get("Retry-After") || null;
        throw err;
      }

      const encoded = data?.predictions?.[0]?.bytesBase64Encoded;
      if (!encoded) {
        const err = new Error("No image data returned from Imagen");
        err.userMessage = "Imagen returned no image data. Please try again.";
        throw err;
      }

      return `data:image/png;base64,${encoded}`;
    } catch (err) {
      const retryableNetwork = err?.name === "AbortError" || err instanceof TypeError;
      const shouldRetry = err?.retryable || retryableNetwork;
      if (i < retries - 1 && shouldRetry) {
        const is429 = err?.status === 429;
        const is503 = err?.status === 503;

        // Honour Retry-After header if present (Google sends this on 429s)
        const retryAfterSec = err?.retryAfter ? parseFloat(err.retryAfter) : null;
        const retryAfterMs  = retryAfterSec && !isNaN(retryAfterSec)
          ? Math.min(retryAfterSec * 1000, 60000) : null;

        // Wide jitter on 429/503 — spreads concurrent users across a 15s window
        // instead of a 3s window, dramatically reducing retry pile-ups at scale
        const baseWait = retryAfterMs || (is429 || is503 ? Math.max(delay, 5000) : delay);
        const jitterRange = is429 || is503 ? 10000 : 1500;
        const jitter = Math.floor(Math.random() * jitterRange);
        await sleep(baseWait + jitter);
        delay = Math.min(delay * 2, 45000);
        continue;
      }
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }
  }
}
